import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { AdminAccountSummary } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useAccountsList } from '../api/useAdmin.ts';
import { formatUsdCents } from '../components/formatters.ts';
import { StatusChip } from '../components/StatusChip.tsx';
import { useT } from '../i18n/index.ts';

/**
 * `/customers/accounts` — tenant accounts list.
 *
 * DESIGN.md §6 Data tables: zebra via `surface-container` vs
 * `surface-container-low`, no horizontal dividers, `label-md` uppercase
 * column headers.
 *
 * Operator affordances:
 *   - Server-side search (email LIKE) — submit on Enter or button click.
 *   - `Has balance` toggle — filters to accounts with non-zero balance.
 *   - Pagination (limit=50). Offset-only; total comes from the response.
 *   - Row click → `/customers/accounts/:accountId` detail.
 *
 * The search/toggle state drives the query scope tuple that's baked into
 * the React Query cache key, so each (email, hasBalance, offset) trio gets
 * its own cache slot. Pagination works by mutating `offset` — we don't
 * show a spinner on re-fetch; TanStack Query keeps the old page rendered
 * and the "Next / Previous" buttons disable while pending.
 */

const PAGE_LIMIT = 50;

function formatRelative(iso: string): string {
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) {
    return '—';
  }
  const deltaMs = Date.now() - parsed;
  const deltaSeconds = Math.round(deltaMs / 1000);
  if (deltaSeconds < 60) return `${Math.max(deltaSeconds, 0)}s ago`;
  const deltaMinutes = Math.round(deltaSeconds / 60);
  if (deltaMinutes < 60) return `${deltaMinutes}m ago`;
  const deltaHours = Math.round(deltaMinutes / 60);
  if (deltaHours < 24) return `${deltaHours}h ago`;
  const deltaDays = Math.round(deltaHours / 24);
  if (deltaDays < 30) return `${deltaDays}d ago`;
  const deltaMonths = Math.round(deltaDays / 30);
  if (deltaMonths < 12) return `${deltaMonths}mo ago`;
  const deltaYears = Math.round(deltaMonths / 12);
  return `${deltaYears}y ago`;
}

function truncateId(value: string | null, head = 8, tail = 4): string {
  if (value === null) return '—';
  if (value.length <= head + tail + 3) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

interface RowProps {
  readonly row: AdminAccountSummary;
  readonly zebra: 'odd' | 'even';
  readonly systemLabel: string;
  readonly tenantLabel: string;
}

function AccountRow({ row, zebra, systemLabel, tenantLabel }: RowProps): JSX.Element {
  const zebraClass = zebra === 'odd' ? 'bg-surface-container' : 'bg-surface-container-low';
  const detailPath = `/customers/accounts/${encodeURIComponent(row.account_id)}`;
  return (
    <tr
      data-testid={`account-row-${row.account_id}`}
      className={`${zebraClass} transition-colors hover:bg-surface-bright`}
    >
      <td className="px-4 py-3 text-[13px] font-semibold text-on-surface">
        <Link
          to={detailPath}
          className="hover:text-primary focus-visible:outline-none focus-visible:underline"
          data-testid={`account-row-${row.account_id}-link`}
        >
          {row.email}
        </Link>
      </td>
      <td
        className="px-4 py-3 font-mono text-[12px] text-on-surface-variant"
        title={row.org_id ?? ''}
      >
        {truncateId(row.org_id)}
      </td>
      <td
        className="px-4 py-3 text-right font-mono text-[13px] font-semibold text-on-surface"
        data-testid={`account-row-${row.account_id}-balance`}
      >
        {formatUsdCents(row.balance_usd_cents)}
      </td>
      <td className="px-4 py-3">
        {row.is_system ? (
          <StatusChip
            variant="neutral"
            label={systemLabel}
            testId={`account-row-${row.account_id}-kind`}
          />
        ) : (
          <StatusChip
            variant="ok"
            label={tenantLabel}
            testId={`account-row-${row.account_id}-kind`}
          />
        )}
      </td>
      <td className="px-4 py-3 text-[12px] text-on-surface-variant">
        {formatRelative(row.created_at)}
      </td>
    </tr>
  );
}

interface Scope {
  readonly email: string;
  readonly hasBalance: boolean;
  readonly offset: number;
}

export function AccountsList(): JSX.Element {
  const t = useT();
  const [draftEmail, setDraftEmail] = useState('');
  const [scope, setScope] = useState<Scope>({ email: '', hasBalance: false, offset: 0 });

  const query = useAccountsList({
    limit: PAGE_LIMIT,
    offset: scope.offset,
    email: scope.email,
    hasBalance: scope.hasBalance,
  });

  function handleSearch(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setScope((prev) => ({ ...prev, email: draftEmail.trim(), offset: 0 }));
  }

  function handleToggleHasBalance(checked: boolean): void {
    setScope((prev) => ({ ...prev, hasBalance: checked, offset: 0 }));
  }

  function handleClear(): void {
    setDraftEmail('');
    setScope({ email: '', hasBalance: false, offset: 0 });
  }

  const total = query.data?.total ?? 0;
  const currentOffset = query.data?.offset ?? scope.offset;
  const currentLimit = query.data?.limit ?? PAGE_LIMIT;
  const rows = useMemo(() => query.data?.accounts ?? [], [query.data]);
  const hasPrev = currentOffset > 0;
  const hasNext = currentOffset + currentLimit < total;

  return (
    <section data-testid="accounts-list-page" className="flex flex-col gap-8">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-2">
          <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
            {t('accounts.title')}
          </h2>
          <p className="text-sm text-outline">{t('accounts.subtitle')}</p>
        </div>
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={handleSearch}
          noValidate
          data-testid="accounts-search-form"
        >
          <label className="flex h-10 items-center gap-2 rounded-lg bg-surface-container-lowest px-3 focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 focus-within:ring-offset-surface">
            <span
              aria-hidden="true"
              className="material-symbols-outlined text-[16px] text-outline"
            >
              search
            </span>
            <input
              type="search"
              value={draftEmail}
              onChange={(e) => setDraftEmail(e.currentTarget.value)}
              placeholder={t('accounts.search.placeholder')}
              aria-label={t('accounts.search.aria_label')}
              data-testid="accounts-search-input"
              className="w-56 bg-transparent text-[13px] text-on-surface placeholder:text-outline focus:outline-none"
            />
          </label>
          <label className="inline-flex h-10 items-center gap-2 rounded-lg bg-surface-container-lowest px-3 text-[12px] font-semibold tracking-tight text-on-surface">
            <input
              type="checkbox"
              checked={scope.hasBalance}
              onChange={(e) => handleToggleHasBalance(e.currentTarget.checked)}
              data-testid="accounts-has-balance"
              className="h-4 w-4 rounded border-outline-variant bg-surface-container-lowest text-primary focus-visible:ring-2 focus-visible:ring-primary"
            />
            <span>{t('accounts.has_balance')}</span>
          </label>
          <button
            type="submit"
            data-testid="accounts-search-submit"
            className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
          >
            {t('accounts.search.submit')}
          </button>
          {scope.email.length > 0 || scope.hasBalance ? (
            <button
              type="button"
              onClick={handleClear}
              data-testid="accounts-search-clear"
              className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-outline transition-colors hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {t('accounts.search.clear')}
            </button>
          ) : null}
        </form>
      </header>

      {query.isError ? (
        <div
          role="alert"
          data-testid="accounts-error"
          className="flex items-center justify-between gap-4 rounded-xl bg-error-container px-6 py-4 text-on-error-container"
        >
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold">{t('accounts.error.title')}</span>
            <span className="text-xs">
              {query.error instanceof OpsApiError
                ? `${query.error.code}: ${query.error.message}`
                : t('common.network_error')}
            </span>
          </div>
          <button
            type="button"
            onClick={() => void query.refetch()}
            data-testid="accounts-retry"
            className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-error-container"
          >
            {t('common.retry')}
          </button>
        </div>
      ) : null}

      {query.isPending ? (
        <div
          data-testid="accounts-loading"
          className="flex flex-col gap-2 rounded-xl bg-surface-container p-4"
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-12 animate-pulse rounded-lg bg-surface-container-low"
              aria-hidden="true"
            />
          ))}
          <span className="sr-only">{t('accounts.loading_aria')}</span>
        </div>
      ) : null}

      {query.isSuccess && rows.length === 0 ? (
        <div
          data-testid="accounts-empty"
          className="flex flex-col items-center gap-4 rounded-xl bg-surface-container px-8 py-16 text-center"
        >
          <h3 className="text-2xl font-semibold tracking-tight text-on-surface">
            {scope.email.length > 0 || scope.hasBalance
              ? t('accounts.empty.filtered.title')
              : t('accounts.empty.initial.title')}
          </h3>
          <p className="max-w-md text-sm text-outline">
            {scope.email.length > 0 || scope.hasBalance
              ? t('accounts.empty.filtered.body')
              : t('accounts.empty.initial.body')}
          </p>
        </div>
      ) : null}

      {query.isSuccess && rows.length > 0 ? (
        <>
          <div className="overflow-hidden rounded-xl bg-surface-container">
            <table
              data-testid="accounts-table"
              className="w-full border-separate border-spacing-0"
            >
              <thead>
                <tr className="bg-surface-container-high">
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                  >
                    {t('accounts.column.email')}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                  >
                    {t('accounts.column.org_id')}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                  >
                    {t('accounts.column.balance')}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                  >
                    {t('accounts.column.kind')}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                  >
                    {t('accounts.column.created')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <AccountRow
                    key={row.account_id}
                    row={row}
                    zebra={i % 2 === 0 ? 'odd' : 'even'}
                    systemLabel={t('accounts.chip.system')}
                    tenantLabel={t('accounts.chip.tenant')}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <nav
            aria-label={t('accounts.pagination.aria_label')}
            data-testid="accounts-pagination"
            className="flex items-center justify-between gap-4 rounded-xl bg-surface-container-low px-6 py-4 text-[12px] text-outline"
          >
            <span>
              {t('accounts.pagination.range', {
                start: currentOffset + 1,
                end: Math.min(currentOffset + rows.length, total),
                total,
              })}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={!hasPrev || query.isFetching}
                onClick={() =>
                  setScope((prev) => ({
                    ...prev,
                    offset: Math.max(0, prev.offset - PAGE_LIMIT),
                  }))
                }
                data-testid="accounts-pagination-prev"
                className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40 disabled:pointer-events-none"
              >
                {t('common.previous')}
              </button>
              <button
                type="button"
                disabled={!hasNext || query.isFetching}
                onClick={() =>
                  setScope((prev) => ({ ...prev, offset: prev.offset + PAGE_LIMIT }))
                }
                data-testid="accounts-pagination-next"
                className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40 disabled:pointer-events-none"
              >
                {t('common.next')}
              </button>
            </div>
          </nav>
        </>
      ) : null}
    </section>
  );
}
